<template>
  <div class="checkout-view">
    <AppHeader />
    
    <div class="checkout-container">
      <div v-if="!orderPlaced" class="checkout-content">
        <div class="page-header">
          <h1>{{ $t('checkout') }}</h1>
          <p>{{ $t('reviewOrder') }}</p>
        </div>
        
        <div class="summary-card">
          <div class="summary-header">
            <h2>{{ $t('orderSummary') }}</h2>
            <span class="item-count">{{ tableStore.itemCount }} {{ $t('items') }}</span>
          </div>

          <div class="summary-items">
            <div v-for="(group, userName) in groupedItems" :key="userName" class="user-group">
              <div class="user-header">
                <div class="user-avatar">{{ getInitials(userName) }}</div>
                <span class="user-name">{{ userName }}</span>
              </div>

              <div v-for="item in group" :key="item.id" class="summary-item">
                <div class="item-content">
                  <div class="item-main">
                    <div class="item-info">
                      <span class="item-qty">{{ item.quantity }}x</span>
                      <div class="item-name-price">
                        <span class="item-name">{{ item.name }}</span>
                        <span class="base-price">{{ $t('basePrice') }}: {{ formatPrice(getBasePrice(item)) }}</span>
                      </div>
                    </div>
                    <span class="item-price">{{ formatPrice(item.price * item.quantity) }}</span>
                  </div>
                  
                  <div class="item-details" v-if="hasExtras(item) || item.customizations?.size">
                    <p class="item-variant" v-if="item.customizations?.size">
                      {{ $t(item.customizations.size.nameKey) || item.customizations.size.id }}
                      <span v-if="item.customizations.size.priceModifier > 0">
                        (+ {{ formatPrice(item.customizations.size.priceModifier) }})
                      </span>
                    </p>
                    <div class="item-extras" v-if="hasExtras(item)">
                      <span v-if="item.customizations?.cheese">
                        + {{ $t(item.customizations.cheese.nameKey) || item.customizations.cheese.id }}
                        <template v-if="item.customizations.cheese.priceModifier > 0">
                          ({{ formatPrice(item.customizations.cheese.priceModifier) }})
                        </template>
                      </span>
                      <template v-if="item.customizations?.sauces">
                        <span v-for="sauce in item.customizations.sauces" :key="sauce.id || sauce">
                          + {{ $t(sauce.nameKey || '') || sauce.id || sauce }}
                          <template v-if="sauce.priceModifier > 0">
                            ({{ formatPrice(sauce.priceModifier) }})
                          </template>
                        </span>
                      </template>
                      <template v-if="item.customizations?.addIngredients">
                        <span v-for="ing in item.customizations.addIngredients" :key="ing.id || ing">
                          + {{ getOptionName(ing) }}
                          <template v-if="ing.price > 0">
                            ({{ formatPrice(ing.price) }})
                          </template>
                        </span>
                      </template>
                      <template v-if="item.customizations?.removeIngredients">
                        <span v-for="ing in item.customizations.removeIngredients" :key="ing.id || ing" class="removed-ingredient">
                          − {{ getOptionName(ing) }}
                        </span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="summary-row total">
            <span>{{ $t('total') }}</span>
            <span class="total-amount">{{ formatPrice(tableStore.cartTotal) }}</span>
          </div>
        </div>

        <div class="actions">
          <div v-if="!canCheckout" class="host-only-notice">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            <span>{{ $t('onlyHostCanCheckout') }}</span>
          </div>
          
          <!-- Dintero Payment Button -->
          <button 
            @click="goToPayment" 
            class="btn-primary place-order-btn dintero-btn" 
            :disabled="isOrdering || tableStore.cartItems.length === 0 || !canCheckout"
          >
            <span v-if="isOrdering" class="spinner"></span>
            <span v-else>{{ $t('proceedToPayment') }} • {{ formatPrice(tableStore.cartTotal) }}</span>
          </button>
          
          <!-- Keep legacy order button as secondary option (for testing/development) -->
          <button 
            @click="placeOrder" 
            class="btn-secondary place-order-legacy" 
            :disabled="isOrdering || tableStore.cartItems.length === 0 || !canCheckout"
          >
            <span v-if="isOrdering" class="spinner"></span>
            <span v-else>{{ $t('placeOrder') }} ({{ $t('noPay') }})</span>
          </button>
          
          <button 
            v-if="sessionStore.mode === 'collaborative'" 
            class="change-host-link" 
            @click="showUsersModal = true"
          >
            {{ $t('changeWhoChecksOut') }}
          </button>
          <button @click="router.back()" class="btn-secondary back-btn">
            {{ $t('backToMenu') }}
          </button>
        </div>
      </div>

      <div v-else class="success-state">
        <div class="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2>{{ $t('orderPlacedSuccess') }}</h2>
        <p>{{ $t('orderSentMessage') }}</p>
        <button @click="backToMenu" class="btn-primary">{{ $t('orderMore') }}</button>
      </div>
    </div>

    <!-- Users Modal -->
    <transition name="modal-fade">
      <div v-if="showUsersModal" class="modal-overlay" @click="showUsersModal = false">
        <div class="users-modal" @click.stop>
          <div class="modal-header">
            <h3>{{ $t('tableParticipants') }}</h3>
            <button class="close-btn" @click="showUsersModal = false">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="users-list">
            <div v-for="user in allParticipants" :key="user.id" class="user-row">
              <div class="user-avatar-lg" :class="{ host: user.isHost }">
                {{ user.name.charAt(0).toUpperCase() }}
              </div>
              <div class="user-details">
                <span class="user-name-lg">{{ user.name }}</span>
                <div class="user-badges">
                  <span v-if="user.isHost" class="badge host-badge">{{ $t('host') }}</span>
                  <span v-if="user.isOnline" class="badge online-badge">{{ $t('online') }}</span>
                  <span v-else class="badge offline-badge">{{ $t('offline') }}</span>
                  <span v-if="user.itemCount > 0" class="badge items-badge">{{ user.itemCount }} {{ $t('items') }}</span>
                </div>
              </div>
              <button 
                v-if="isCurrentUserHost && !user.isHost" 
                class="make-host-btn"
                @click="transferHost(user)"
                :disabled="isTransferring"
              >
                {{ $t('makeHost') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTableStore } from '@/stores/table'
import { useSessionStore } from '@/stores/session'
import { useMenuStore } from '@/stores/menu'
import { formatPrice } from '@/utils/formatters'
import AppHeader from '@/components/AppHeader.vue'

const router = useRouter()
const tableStore = useTableStore()
const sessionStore = useSessionStore()
const menuStore = useMenuStore()
const isOrdering = ref(false)
const orderPlaced = ref(false)
const showUsersModal = ref(false)
const isTransferring = ref(false)

// Only host can checkout in collaborative mode (read from Firebase-synced users)
const canCheckout = computed(() => {
  if (sessionStore.mode === 'single') return true
  if (!sessionStore.currentUser?.id) return false
  const userFromFirebase = tableStore.users[sessionStore.currentUser.id]
  return userFromFirebase?.isHost === true
})

// Check if current user is the host
const isCurrentUserHost = computed(() => {
  if (!sessionStore.currentUser?.id) return false
  const userFromFirebase = tableStore.users[sessionStore.currentUser.id]
  return userFromFirebase?.isHost === true
})

// Combine active users with users who have added items to cart
const allParticipants = computed(() => {
  const participants = new Map()
  
  // Add all registered users from the session
  Object.values(tableStore.users).forEach(user => {
    participants.set(user.id, {
      ...user,
      isOnline: false,
      itemCount: 0
    })
  })
  
  // Mark active users as online
  tableStore.activeUsers.forEach(user => {
    if (participants.has(user.id)) {
      participants.get(user.id).isOnline = true
    } else {
      participants.set(user.id, {
        ...user,
        isOnline: true,
        itemCount: 0
      })
    }
  })
  
  // Count items per user from cart
  tableStore.cartItems.forEach(item => {
    const userId = item.addedBy
    if (participants.has(userId)) {
      participants.get(userId).itemCount += item.quantity
    } else {
      participants.set(userId, {
        id: userId,
        name: item.addedByName || 'Unknown',
        isHost: false,
        isOnline: false,
        itemCount: item.quantity
      })
    }
  })
  
  // Sort: host first, then online users, then by name
  return Array.from(participants.values()).sort((a, b) => {
    if (a.isHost !== b.isHost) return a.isHost ? -1 : 1
    if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1
    return a.name.localeCompare(b.name)
  })
})

const transferHost = async (newHost) => {
  if (!isCurrentUserHost.value || isTransferring.value) return
  
  isTransferring.value = true
  try {
    await tableStore.transferHost(sessionStore.currentUser.id, newHost.id)
  } catch (error) {
    console.error('Failed to transfer host:', error)
    alert('Failed to transfer host. Please try again.')
  } finally {
    isTransferring.value = false
  }
}

const groupedItems = computed(() => {
  const groups = {}
  tableStore.cartItems.forEach(item => {
    const name = item.addedByName || 'Guest'
    if (!groups[name]) {
      groups[name] = []
    }
    groups[name].push(item)
  })
  return groups
})

const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const getBasePrice = (item) => {
  const product = menuStore.getItemById(item.productId)
  return product ? product.price : 0
}

const hasExtras = (item) => {
  const c = item.customizations
  if (!c) return false
  return (
    c.cheese || 
    (c.sauces && c.sauces.length > 0) || 
    (c.addIngredients && c.addIngredients.length > 0) ||
    (c.removeIngredients && c.removeIngredients.length > 0)
  )
}

/**
 * Get display name for an option
 * For API data: use name directly
 * For static data: try i18n, fallback to name/id
 */
const getOptionName = (option) => {
  if (menuStore.isFromApi && option.name) {
    return option.name
  }
  return option.nameKey || option.name || option.id
}

const placeOrder = async () => {
  if (isOrdering.value) return
  
  isOrdering.value = true
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    await tableStore.clearCart()
    orderPlaced.value = true
  } catch (error) {
    console.error('Failed to place order:', error)
    alert('Failed to place order. Please try again.')
  } finally {
    isOrdering.value = false
  }
}

const goToPayment = () => {
  console.log('goToPayment clicked!')
  // Navigate to the Dintero payment page with restaurant context
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/payment`)
}

const backToMenu = () => {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/menu`)
}

onMounted(() => {
  if (sessionStore.sessionId) {
    tableStore.bindSession(sessionStore.sessionId)
  } else {
    const slug = sessionStore.restaurantSlug || 'demo'
    router.push(`/${slug}`)
  }
})

onUnmounted(() => {
  tableStore.unbindTable()
})
</script>

<style scoped>
.checkout-view {
  min-height: 100vh;
  background: var(--bg-color);
  padding-bottom: 40px;
  padding-top: 80px; /* Space for fixed header */
}

.checkout-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
  text-align: center;
}

.page-header h1 {
  font-size: 2rem;
  color: var(--secondary-color);
  margin-bottom: 8px;
}

.page-header p {
  color: var(--text-light);
}

.summary-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 24px;
  box-shadow: var(--shadow-md);
  margin-bottom: 24px;
  animation: slideUp 0.4s ease-out;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
}

.summary-header h2 {
  font-size: 1.25rem;
  margin: 0;
  color: var(--secondary-color);
}

.item-count {
  background: #F1F5F9;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--text-light);
  font-weight: 600;
}

.summary-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-qty {
  font-weight: 600;
  color: var(--primary-color);
  background: rgba(255, 107, 0, 0.1);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0.9rem;
}

.item-name {
  font-weight: 500;
  color: var(--secondary-color);
}

.item-name-price {
  display: flex;
  flex-direction: column;
}

.base-price {
  font-size: 0.75rem;
  color: var(--text-light);
}

.item-price {
  font-weight: 600;
  color: var(--text-color);
}

.user-group {
  margin-bottom: 24px;
}

.user-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-left: 4px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
}

.user-name {
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 0.95rem;
}

.item-content {
  width: 100%;
}

.item-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.item-details {
  margin-top: 8px;
  padding-left: 40px; /* Align with name */
}

.item-variant {
  font-size: 0.85rem;
  color: var(--text-light);
  margin-bottom: 4px;
}

.item-extras {
  font-size: 0.8rem;
  color: var(--text-light);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.item-extras span {
  background: #F8FAFC;
  padding: 2px 6px;
  border-radius: 4px;
}

.item-extras .removed-ingredient {
  background: #FEE2E2;
  color: #DC2626;
}

.divider {
  height: 1px;
  background: var(--border-color);
  margin: 20px 0;
  border-style: dashed;
}

.summary-row.total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--secondary-color);
}

.total-amount {
  color: var(--primary-color);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.host-only-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #FEF3C7;
  color: #92400E;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
}

.host-only-notice svg {
  flex-shrink: 0;
  color: #D97706;
}

.place-order-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.place-order-btn {
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.3);
}

.place-order-btn.dintero-btn {
  background: linear-gradient(135deg, #FF6B00 0%, #FF8533 100%);
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.4);
}

.place-order-btn.dintero-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #E56200 0%, #FF6B00 100%);
}

.place-order-legacy {
  font-size: 0.85rem;
  padding: 12px 16px;
  opacity: 0.8;
}

.btn-secondary {
  background: white;
  border: 2px solid var(--border-color);
  padding: 16px;
  border-radius: var(--radius-md);
  font-weight: 600;
  color: var(--text-light);
  transition: all 0.2s;
  width: 100%;
}

.btn-secondary:hover {
  border-color: var(--secondary-color);
  color: var(--secondary-color);
}

.change-host-link {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  transition: opacity 0.2s;
}

.change-host-link:hover {
  opacity: 0.7;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 40px 20px;
  animation: fadeIn 0.5s ease-out;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: var(--success-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.success-icon svg {
  width: 40px;
  height: 40px;
}

.success-state h2 {
  font-size: 2rem;
  color: var(--secondary-color);
  margin-bottom: 12px;
}

.success-state p {
  color: var(--text-light);
  margin-bottom: 32px;
  font-size: 1.1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes popIn {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Users Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 200;
}

.users-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  max-height: 70vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--secondary-color);
}

.close-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.users-list {
  padding: 12px;
  overflow-y: auto;
  max-height: calc(70vh - 60px);
}

.user-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  transition: background 0.2s;
}

.user-row:hover {
  background: #f8f9fa;
}

.make-host-btn {
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.make-host-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
}

.make-host-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.user-avatar-lg {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  flex-shrink: 0;
}

.user-avatar-lg.host {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.4);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name-lg {
  font-weight: 600;
  color: var(--secondary-color);
  display: block;
  margin-bottom: 4px;
}

.user-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.host-badge {
  background: #FEF3C7;
  color: #92400E;
}

.online-badge {
  background: #D1FAE5;
  color: #065F46;
}

.offline-badge {
  background: #F3F4F6;
  color: #6B7280;
}

.items-badge {
  background: #EDE9FE;
  color: #5B21B6;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
